import React, { useEffect, useState } from 'react';
import { SidePanel } from './SidePanel';
import { X } from 'lucide-react';
import { cashflowApi } from '../services/cashflowApi';
import { Cashflow, CashflowForm, FEE_KINDS, FEE_LABEL, FLOW_KINDS, KIND_META } from '../types/cashflow';
import { Participant, Vehicle, KIND_LABEL } from '../types/vehicle';
import { Deal } from '../types/deal';
import { Company } from '../types/company';
import { todayLocal } from '../lib/date';

/**
 * 원장 기록 추가 — 원장 · 투자 건 상세 · 자금원 상세가 같이 쓴다.
 *
 * ⚠️ 날짜는 **통장 입출금일**이다. 계약일을 적으면 수익률이 틀린다.
 * ⚠️ 종류가 무엇을 같이 골라야 하는지(건 · 참여자)는 `KIND_META.needs` 가 정한다.
 */
const EMPTY = (initial?: Partial<CashflowForm>): CashflowForm => ({
  vehicleId: null, kind: 'investment', dealId: null, participantId: null,
  feeKind: 'mgmt', amount: null, occurredOn: todayLocal(), memo: '',
  ...initial,
});

export const CashflowFormModal: React.FC<{
  open: boolean; vehicles: Vehicle[]; parts: Participant[]; deals: Deal[]; companies: Company[];
  /** 미리 채울 값 — 투자 건 상세에서 열면 자금원·건이 정해져 있다 */
  initial?: Partial<CashflowForm>;
  /** 고칠 기록 — 있으면 「기록 수정」 · 저장은 PATCH · 아래에 지우기 */
  editing?: Cashflow | null;
  onClose: () => void; onSaved: () => void;
}> = ({ open, vehicles, parts, deals, companies, initial, editing = null, onClose, onSaved }) => {
  const fromFlow = (f: Cashflow): Partial<CashflowForm> => ({
    vehicleId: f.vehicleId, kind: f.kind, dealId: f.dealId, participantId: f.participantId,
    feeKind: f.feeKind ?? 'mgmt', amount: f.amount, occurredOn: f.occurredOn, memo: f.memo ?? '',
  });
  const [form, setForm] = useState<CashflowForm>(() => EMPTY(editing ? fromFlow(editing) : initial));
  // 지우기는 두 번 눌러야 — 돈 기록이라 실수로 지우면 수익률이 조용히 바뀐다
  const [armed, setArmed] = useState(false);
  const [saving, setSaving] = useState(false);
  // 실패하면 창 안에 이유를 보인다 — 던지면 단추가 아무 반응 없이 멈춘다(실사용 테스트 7회차)
  const [err, setErr] = useState<string | null>(null);

  // 열 때마다 새로 채운다. initial 은 여는 쪽이 매번 새 객체로 넘기므로 open 만 본다
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) { setForm(EMPTY(editing ? fromFlow(editing) : initial)); setArmed(false); setErr(null); } }, [open, editing]);
  if (!open) return null;

  const meta = KIND_META[form.kind];
  const set = <K extends keyof CashflowForm>(k: K, v: CashflowForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const input = 'w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:border-indigo-400 focus:outline-none';
  const label = 'block text-xs font-medium text-slate-500 mb-1';

  // 고른 자금원에 딸린 것만 보여준다 — 다른 지갑의 건을 고르면 원장이 틀린다
  const myDeals = deals.filter((d) => d.vehicleId === form.vehicleId);
  const myParts = parts.filter((p) => p.vehicleId === form.vehicleId);
  const cName = (id: number) => companies.find((c) => c.id === id)?.name ?? '—';

  const needsOk =
    meta.needs === 'none' ||
    (meta.needs === 'deal' && form.dealId !== null) ||
    (meta.needs === 'participant' && form.participantId !== null);
  const can = form.vehicleId !== null && (form.amount ?? 0) > 0 && form.occurredOn && needsOk && !saving;

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      if (editing) await cashflowApi.update(editing.id, form); else await cashflowApi.create(form);
      onSaved();
    } catch (e) { setErr((e as Error).message); } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!editing) return;
    if (!armed) { setArmed(true); return; }
    setSaving(true); setErr(null);
    try { await cashflowApi.remove(editing.id); onSaved(); } catch (e) { setErr((e as Error).message); } finally { setSaving(false); }
  };

  return (
    <SidePanel onClose={onClose} bare>
      <div className="flex flex-col min-h-full">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-bold text-slate-900">{editing ? '기록 수정' : '기록 추가'}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div>
            <label className={label} htmlFor="f-vehicle">자금원</label>
            <select id="f-vehicle" className={input} value={form.vehicleId ?? ''}
              onChange={(e) => setForm((f) => ({
                ...f,
                vehicleId: e.target.value === '' ? null : Number(e.target.value),
                // 지갑이 바뀌면 상대를 지운다 — 다른 지갑의 건이 남으면 안 된다
                dealId: null, participantId: null,
              }))}>
              <option value="">선택</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>{v.name} ({KIND_LABEL[v.kind]})</option>
              ))}
            </select>
          </div>

          <div>
            <label className={label}>거래 구분</label>
            <div className="grid grid-cols-3 gap-1.5">
              {FLOW_KINDS.map((k) => (
                <button key={k} type="button"
                  onClick={() => setForm((f) => ({ ...f, kind: k, dealId: null, participantId: null }))}
                  className={`px-2 py-1.5 rounded-lg text-xs font-medium border ${
                    form.kind === k
                      ? 'bg-slate-900 border-slate-900 text-white'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}>
                  {KIND_META[k].label}
                </button>
              ))}
            </div>
            <p className={`mt-1.5 text-xs font-medium ${meta.direction === 'in' ? 'text-emerald-700' : 'text-slate-500'}`}>
              {meta.direction === 'in' ? '입금' : '출금'}
            </p>
          </div>

          {meta.needs === 'deal' && (
            <div>
              <label className={label} htmlFor="f-deal">투자 건</label>
              <select id="f-deal" className={input} value={form.dealId ?? ''} disabled={form.vehicleId === null}
                onChange={(e) => set('dealId', e.target.value === '' ? null : Number(e.target.value))}>
                <option value="">{form.vehicleId === null ? '자금원을 먼저 선택' : '선택'}</option>
                {myDeals.map((d) => <option key={d.id} value={d.id}>{cName(d.companyId)}</option>)}
              </select>
              {form.vehicleId !== null && myDeals.length === 0 && (
                <p className="mt-1 text-xs text-amber-700">이 자금원에는 투자 건이 아직 없습니다.</p>
              )}
            </div>
          )}

          {meta.needs === 'participant' && (
            <div>
              <label className={label} htmlFor="f-part">출자자</label>
              <select id="f-part" className={input} value={form.participantId ?? ''} disabled={form.vehicleId === null}
                onChange={(e) => set('participantId', e.target.value === '' ? null : Number(e.target.value))}>
                <option value="">{form.vehicleId === null ? '자금원을 먼저 선택' : '선택'}</option>
                {myParts.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}{p.isManager ? ' (운용)' : ''}</option>
                ))}
              </select>
            </div>
          )}

          {form.kind === 'fee' && (
            <div>
              <label className={label}>보수 구분</label>
              <div className="grid grid-cols-2 gap-1.5">
                {FEE_KINDS.map((k) => (
                  <button key={k} type="button" onClick={() => set('feeKind', k)}
                    className={`px-2 py-1.5 rounded-lg text-xs font-medium border ${form.feeKind === k ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                    {FEE_LABEL[k]}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label} htmlFor="f-amount">금액 (원)</label>
              <input id="f-amount" type="number" min={0} step={1000000} className={input + ' text-right'}
                value={form.amount ?? ''}
                onChange={(e) => set('amount', e.target.value === '' ? null : Number(e.target.value))} />
            </div>
            <div>
              <label className={label} htmlFor="f-date">통장 입출금일</label>
              <input id="f-date" type="date" max={todayLocal()} className={input} value={form.occurredOn}
                onChange={(e) => set('occurredOn', e.target.value)} />
            </div>
          </div>

          <div>
            <label className={label} htmlFor="f-memo">비고</label>
            <input id="f-memo" className={input} value={form.memo} onChange={(e) => set('memo', e.target.value)} />
          </div>
        </div>

        <div className="mt-auto sticky bottom-0 bg-white flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
          {editing && (
            <button onClick={remove} onBlur={() => setArmed(false)} disabled={saving}
              className={`mr-auto px-3 py-2 rounded-lg text-sm font-medium border ${armed ? 'bg-red-600 border-red-600 text-white' : 'border-red-200 text-red-600 hover:bg-red-50'}`}>
              {armed ? '한 번 더 누르면 지웁니다' : '이 기록 지우기'}
            </button>
          )}
          {err && <p role="alert" className={`${editing ? '' : 'mr-auto'} text-xs text-red-600`}>{err}</p>}
          <button onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50">취소</button>
          <button onClick={save} disabled={!can}
            className="px-5 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">
            {saving ? '저장 중...' : editing ? '저장' : '등록'}
          </button>
        </div>
      </div>
    </SidePanel>
  );
};
